import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Search, AlertTriangle, CheckCircle, Clock, Users, Building, Calendar, AlertCircle, Expand, RefreshCw } from 'lucide-react';
import apiService, { User, UsersResponse, UserStatistics } from '../services/api';

// Define order status options
const ORDER_STATUS_OPTIONS = [
  { value: '', label: 'Select Status' },
  { value: 'New Order Received', label: 'New Order Received' },
  { value: 'Not Yet Done with Setup', label: 'Not Yet Done with Setup' },
  { value: 'Setup Done and Ready for Upload', label: 'Setup Done and Ready for Upload' },
  { value: 'Uploaded and Ready to Verify', label: 'Uploaded and Ready to Verify' },
  { value: 'Technical Issue', label: 'Technical Issue' },
  { value: 'Technical Issue Resolved', label: 'Technical Issue Resolved' },
  { value: 'Asks for 2FA', label: 'Asks for 2FA' },
  { value: 'Reconnect Request', label: 'Reconnect Request' },
  { value: 'New Emails Requested', label: 'New Emails Requested' },
];

interface UsersTableProps {
  defaultPlatform?: string;
  title?: string;
}

export const UsersTable: React.FC<UsersTableProps> = ({ defaultPlatform, title }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [polling, setPolling] = useState(false);
  const [pollMessage, setPollMessage] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<{ last_processed_date?: string | null; updated_at?: string | null } | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [updatingNotes, setUpdatingNotes] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedUserForNotes, setSelectedUserForNotes] = useState<User | null>(null);
  const [clientNotes, setClientNotes] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [statistics, setStatistics] = useState<UserStatistics>({
    total_clients: 0,
    unique_workspaces: 0,
    pending_domains: 0,
    new_domains_24h: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState(defaultPlatform || '');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('');
  const [domainCreationDateFilter, setDomainCreationDateFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.limit, search, statusFilter, platformFilter, orderStatusFilter, deliveryStatusFilter, domainCreationDateFilter, sortBy, sortOrder]);

  // Keep platform filter in sync with defaultPlatform prop (for route changes)
  useEffect(() => {
    setPlatformFilter(defaultPlatform || '');
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [defaultPlatform]);

  const refreshStatistics = async () => {
    setStatsLoading(true);
    try {
      const response = await apiService.getUserStatistics(platformFilter || undefined);
      if (response.statistics) {
        setStatistics(response.statistics);
      }
    } catch (error) {
      console.error('Failed to refresh statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleManualFetch = async () => {
    setPolling(true);
    setPollMessage(null);
    try {
      const resp = await fetch('/api/smartlead/poll', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reset: true }) });
      const data = await resp.json();
      if (resp.ok && data.success) {
        setPollMessage(`Fetched ${data.processed ?? 0} orders`);
        await fetchUsers();
        await refreshStatistics();
        await fetchPollState();
      } else {
        setPollMessage(data.message || 'Fetch failed');
      }
    } catch (e: any) {
      setPollMessage(e?.message || 'Fetch failed');
    } finally {
      setPolling(false);
      // Clear message after a short delay
      setTimeout(() => setPollMessage(null), 5000);
    }
  };

  const fetchPollState = async () => {
    try {
      const resp = await fetch('/api/smartlead/poll-state');
      const data = await resp.json();
      if (data?.success) {
        setLastFetch({ last_processed_date: data.last_processed_date, updated_at: data.updated_at });
      }
    } catch (e) {
      // best-effort; ignore errors
    }
  };

  // Load last fetch time on mount and every 5 minutes
  useEffect(() => {
    if (platformFilter === 'Smartlead' || defaultPlatform === 'Smartlead') {
      fetchPollState();
      const interval = setInterval(fetchPollState, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [platformFilter, defaultPlatform]);

  // Fetch statistics separately, tied to current platform filter
  useEffect(() => {
    let isMounted = true;
    const fetchStatistics = async () => {
      setStatsLoading(true);
      try {
        const response = await apiService.getUserStatistics(platformFilter || undefined);
        if (response.statistics && isMounted) {
          setStatistics(response.statistics);
        }
      } catch (error) {
        console.error('Failed to fetch statistics:', error);
      } finally {
        if (isMounted) setStatsLoading(false);
      }
    };

    // Fetch immediately and on interval
    fetchStatistics();
    const interval = setInterval(fetchStatistics, 120000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [platformFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: UsersResponse = await apiService.getUsers({
        page: pagination.page,
        limit: pagination.limit,
        search,
        status: statusFilter,
        platform: platformFilter,
        order_status: orderStatusFilter,
        delivery_status: deliveryStatusFilter,
        domain_creation_date: domainCreationDateFilter,
        sortBy,
        sortOrder,
      });
      setUsers(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to fetch users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      ...pagination,
      limit: newLimit,
      page: 1, // Reset to first page when changing limit
    });
  };

  const handleRowClick = (user: User) => {
    navigate(`/users/${user.id}`);
  };

  const handleOrderStatusChange = async (userId: string, newStatus: string, event: React.ChangeEvent<HTMLSelectElement>) => {
    // Stop propagation to prevent row click
    event.stopPropagation();
    
    // Don't save if empty status is selected
    if (!newStatus) {
      return;
    }
    
    try {
      setUpdatingStatus(userId);
      await apiService.updateUserOrderStatus(userId, newStatus);
      
      // Update the local state to reflect the change immediately
      setUsers(users.map(user => 
        user.id === userId ? { ...user, order_status: newStatus } : user
      ));
    } catch (err) {
      console.error('Failed to update order status:', err);
      alert('Failed to update order status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleExpandNotes = (user: User) => {
    setSelectedUserForNotes(user);
    setClientNotes(user.client_notes || '');
    setAdminNotes(user.admin_notes || '');
    setShowNotesModal(true);
  };

  const handleModalNotesClose = () => {
    setShowNotesModal(false);
    setSelectedUserForNotes(null);
    setClientNotes('');
    setAdminNotes('');
  };

  const handleModalNotesSave = async () => {
    if (!selectedUserForNotes) return;
    
    try {
      setUpdatingNotes(selectedUserForNotes.id);
      await apiService.updateUserAllNotes(selectedUserForNotes.id, clientNotes, adminNotes);
      
      // Update the local state to reflect the changes immediately
      setUsers(users.map(user => 
        user.id === selectedUserForNotes.id 
          ? { ...user, client_notes: clientNotes, admin_notes: adminNotes } 
          : user
      ));
      
      handleModalNotesClose();
    } catch (err) {
      console.error('Failed to save notes from modal:', err);
    } finally {
      setUpdatingNotes(null);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTimeET = (dateString?: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short', // shows EST/EDT
    });
  };

  const getDeliveryStatusIcon = (deliveryStatus: string) => {
    switch (deliveryStatus) {
      case 'on_track':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'due_approaching':
        return <Clock className="h-4 w-4 text-amber-500" />;
      case 'overdue':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getDeliveryStatusText = (deliveryStatus: string) => {
    switch (deliveryStatus) {
      case 'on_track':
        return 'On Track';
      case 'due_approaching':
        return 'Due Approaching';
      case 'overdue':
        return 'Overdue';
      default:
        return 'Unknown';
    }
  };

  const getDeliveryStatusBadge = (deliveryStatus: string) => {
    const baseClasses = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium gap-1";
    switch (deliveryStatus) {
      case 'on_track':
        return `${baseClasses} bg-green-500/10 text-green-500`;
      case 'due_approaching':
        return `${baseClasses} bg-amber-500/10 text-amber-500`;
      case 'overdue':
        return `${baseClasses} bg-red-500/10 text-red-500`;
      default:
        return `${baseClasses} bg-muted text-muted-foreground`;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{title || 'Users with Pending Orders'}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {defaultPlatform === 'Pager'
              ? 'Orders submitted through the partner API, including domains with pending setup or partial cancellations.'
              : 'Users who have at least one domain with unverified connection requiring completion.'}
          </p>
          {pollMessage && (
            <div className="mt-2 text-xs text-muted-foreground">{pollMessage}</div>
          )}
        </div>
        {(platformFilter === 'Smartlead' || defaultPlatform === 'Smartlead') && (
          <button
            onClick={handleManualFetch}
            disabled={polling}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-border ${polling ? 'opacity-60 cursor-not-allowed' : 'hover:bg-muted'} transition-colors`}
            title="Fetch new Smartlead orders now"
          >
            <RefreshCw className={`h-4 w-4 ${polling ? 'animate-spin' : ''}`} />
            {polling ? 'Fetching…' : 'Fetch Smartlead Orders'}
          </button>
        )}
        {(platformFilter === 'Smartlead' || defaultPlatform === 'Smartlead') && (
          <div className="text-xs text-muted-foreground mt-2 text-right">
            Last successful fetch: {formatDateTimeET(lastFetch?.updated_at)}
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <Users className="h-4 w-4 text-primary" />
            <span className="text-sm text-muted-foreground">Total Clients</span>
          </div>
          <p className="text-2xl font-bold">
            {statsLoading ? <span className="text-sm">Loading...</span> : statistics.total_clients.toLocaleString()}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <Building className="h-4 w-4 text-blue-500" />
            <span className="text-sm text-muted-foreground">Unique Workspaces</span>
          </div>
          <p className="text-2xl font-bold text-blue-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.unique_workspaces.toLocaleString()}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <span className="text-sm text-muted-foreground">Pending Domains</span>
          </div>
          <p className="text-2xl font-bold text-amber-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.pending_domains.toLocaleString()}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-green-500" />
            <span className="text-sm text-muted-foreground">New Orders (24h)</span>
          </div>
          <p className="text-2xl font-bold text-green-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.new_domains_24h.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          </div>
          <input
            type="text"
            className="input-modern h-10 w-full pl-10 pr-3 text-sm"
            placeholder="Search by Workspace ID, Client Email, or Domain Name"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination({ ...pagination, page: 1 });
            }}
          />
        </div>

        <select
          className="input-modern h-10 px-3 text-sm min-w-[150px]"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPagination({ ...pagination, page: 1 });
          }}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </select>

        <select
          className="input-modern h-10 px-3 text-sm min-w-[150px]"
          value={platformFilter}
          onChange={(e) => {
            setPlatformFilter(e.target.value);
            setPagination({ ...pagination, page: 1 });
          }}
        >
          <option value="">All Platforms</option>
          <option value="Instantly">Instantly</option>
          <option value="Smartlead">Smartlead</option>
          <option value="Pager">Manual Orders</option>
        </select>

        <select
          className="input-modern h-10 px-3 text-sm min-w-[150px]"
          value={orderStatusFilter}
          onChange={(e) => {
            setOrderStatusFilter(e.target.value);
            setPagination({ ...pagination, page: 1 });
          }}
        >
          <option value="">All Order Statuses</option>
          {ORDER_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <select
          className="input-modern h-10 px-3 text-sm min-w-[150px]"
          value={deliveryStatusFilter}
          onChange={(e) => {
            setDeliveryStatusFilter(e.target.value);
            setPagination({ ...pagination, page: 1 });
          }}
        >
          <option value="">All Delivery Statuses</option>
          <option value="on_track">On Track</option>
          <option value="due_approaching">Due Approaching</option>
          <option value="overdue">Overdue</option>
        </select>

        <div className="relative">
          <input
            type="date"
            className="input-modern h-10 px-3 text-sm min-w-[170px]"
            value={domainCreationDateFilter}
            onChange={(e) => {
              setDomainCreationDateFilter(e.target.value);
              setPagination({ ...pagination, page: 1 });
            }}
            title="Filter by domain creation date (shows users with unverified domains created on this date)"
          />
          {domainCreationDateFilter && (
            <button
              onClick={() => {
                setDomainCreationDateFilter('');
                setPagination({ ...pagination, page: 1 });
              }}
              className="absolute right-8 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              title="Clear date filter"
            >
              ✕
            </button>
          )}
        </div>

        <select
          className="input-modern h-10 px-3 text-sm min-w-[180px]"
          value={`${sortBy}_${sortOrder}`}
          onChange={(e) => {
            const value = e.target.value;
            // Handle field names with underscores properly
            if (value.endsWith('_ASC')) {
              const field = value.slice(0, -4); // Remove '_ASC'
              setSortBy(field);
              setSortOrder('ASC');
            } else if (value.endsWith('_DESC')) {
              const field = value.slice(0, -5); // Remove '_DESC'
              setSortBy(field);
              setSortOrder('DESC');
            }
          }}
        >
          <option value="created_at_DESC">Newest Users First</option>
          <option value="created_at_ASC">Oldest Users First</option>
          <option value="pending_domains_DESC">Most Pending Domains</option>
          <option value="pending_domains_ASC">Least Pending Domains</option>
          <option value="earliest_unverified_date_ASC">Earliest Unverified First</option>
          <option value="earliest_unverified_date_DESC">Latest Unverified First</option>
          <option value="email_ASC">Email A-Z</option>
          <option value="email_DESC">Email Z-A</option>
        </select>
      </div>

      {/* Table */}
      <div className="card-modern overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-destructive">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1370px] divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[80px]">
                    Index
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-muted/80 transition-colors w-[250px]"
                    onClick={() => handleSort('workspace_id')}
                  >
                    Workspace ID
                    {sortBy === 'workspace_id' && (
                      <span className="ml-1 text-primary">{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-muted/80 transition-colors w-[280px]"
                    onClick={() => handleSort('email')}
                  >
                    Client Email
                    {sortBy === 'email' && (
                      <span className="ml-1 text-primary">{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-muted/80 transition-colors w-[120px]"
                    onClick={() => handleSort('pending_domains')}
                  >
                    Pending Domains
                    {sortBy === 'pending_domains' && (
                      <span className="ml-1 text-primary">{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[130px]">
                    Total Domains
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[150px]">
                    Delivery Status
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[250px]">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[120px]">
                    Actions
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-muted/80 transition-colors w-[170px]"
                    onClick={() => handleSort('earliest_unverified_date')}
                  >
                    Earliest Unverified Date
                    {sortBy === 'earliest_unverified_date' && (
                      <span className="ml-1 text-primary">{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((user, index) => (
                  <tr
                    key={user.id}
                    className="hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(user)}
                  >
                    <td className="px-6 py-4 text-sm font-medium w-[80px]">
                      {(pagination.page - 1) * pagination.limit + index + 1}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono w-[250px]">
                      <div className="max-w-[240px] truncate" title={user.workspace_id}>
                        {user.workspace_id || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm w-[280px]">
                      <div className="max-w-[270px] truncate" title={user.email}>
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm w-[120px]">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        (user.pending_domains || 0) > 0 
                          ? 'bg-amber-500/10 text-amber-500' 
                          : 'bg-green-500/10 text-green-500'
                      }`}>
                        {user.pending_domains || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium w-[130px]">
                      <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-500">
                        {user.domain_count || user.num_domains || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm w-[150px]">
                      <span className={getDeliveryStatusBadge(user.delivery_status || 'unknown')}>
                        {getDeliveryStatusIcon(user.delivery_status || 'unknown')}
                        {getDeliveryStatusText(user.delivery_status || 'unknown')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm w-[250px]">
                      <select
                        className="input-modern h-8 px-2 text-xs min-w-[220px]"
                        value={user.order_status || ''}
                        onChange={(e) => handleOrderStatusChange(user.id, e.target.value, e)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={updatingStatus === user.id}
                      >
                        {ORDER_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {updatingStatus === user.id && (
                        <div className="mt-1 text-xs text-muted-foreground">Updating...</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm w-[120px]">
                      <button
                        className="btn-outline px-3 py-1 text-xs rounded-md hover:bg-muted transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExpandNotes(user);
                        }}
                        title="View and edit notes"
                      >
                        Notes
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground w-[170px]">
                      {formatDate(user.earliest_unverified_date || '')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border pt-4">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
              className="relative inline-flex items-center rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50 transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              disabled={pagination.page === pagination.totalPages}
              className="relative ml-3 inline-flex items-center rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50 transition-colors"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <p className="text-sm text-muted-foreground">
                Showing <span className="font-medium text-foreground">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-medium text-foreground">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-medium text-foreground">{pagination.total}</span> results
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Show:</span>
                <select
                  className="input-modern h-8 px-2 text-xs min-w-[70px]"
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-muted-foreground">per page</span>
              </div>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-lg shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center rounded-l-lg px-2 py-2 text-muted-foreground border border-border hover:bg-muted focus:z-20 disabled:opacity-50 transition-colors"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold border border-border bg-card">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                  disabled={pagination.page === pagination.totalPages}
                  className="relative inline-flex items-center rounded-r-lg px-2 py-2 text-muted-foreground border border-border hover:bg-muted focus:z-20 disabled:opacity-50 transition-colors"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && selectedUserForNotes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-card rounded-lg shadow-lg max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold">Notes</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedUserForNotes.email}
                </p>
              </div>
              <button
                onClick={handleModalNotesClose}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="flex-1 p-6 space-y-6 overflow-y-auto">
              {/* Client Notes */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  Client Notes
                </label>
                <textarea
                  className="input-modern w-full h-32 px-4 py-3 text-sm border-2 focus:border-primary"
                  placeholder="Add notes about this client..."
                  value={clientNotes}
                  onChange={(e) => setClientNotes(e.target.value)}
                  style={{ resize: 'vertical', minHeight: '120px' }}
                  autoFocus
                />
              </div>

              {/* Admin Notes */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  Admin Notes
                </label>
                <textarea
                  className="input-modern w-full h-32 px-4 py-3 text-sm border-2 focus:border-primary"
                  placeholder="Internal admin notes..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  style={{ resize: 'vertical', minHeight: '120px' }}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/20">
              <button
                onClick={handleModalNotesClose}
                className="px-6 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleModalNotesSave}
                disabled={updatingNotes === selectedUserForNotes?.id}
                className="btn-primary px-6 py-2 text-sm font-medium rounded-lg disabled:opacity-50 min-w-[100px]"
              >
                {updatingNotes === selectedUserForNotes?.id ? (
                  <div className="inline-flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  'Save Notes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 